import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import { Popup } from "components"
import { Title, Checkbox } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge from "components/Lims/StatusBadge"
import { assets, limsConsumables, limsSampleWorklists, limsWorklistTemplates, message } from "state_management"
import { SAMPLE_WORKLIST_STATES } from "state_management/lims/limsSampleWorklists"
import "./index.scss"

// WorklistModal (F9, SPEC-041; read-only instrument + consumables: SPEC-042) — the per-sample
// sample-prep worklist popup, opened from each sample card in the ResultEntryPanel. It
// fetch-or-creates the worklist for (sampleId, analysisId), shows the effective template's steps as
// a checklist (+ per-step note) and the "det der er brugt" context (forskrift version + the
// analysis's instrument + consumables, all READ-ONLY).
//
// The instrument and the consumables BELONG TO THE ANALYSIS (chosen in the analysis config) — the
// worklist only DISPLAYS them; the analyst does not pick or re-enter them here. A DRAFT shows the
// analysis's live selection (primaryAssetId + reagentLotIds, passed down from the panel); a SIGNED
// worklist shows the frozen audit snapshot (equipmentAssetId + consumableSnapshot, frozen
// server-side at sign from the analysis). The ANALYST signs it (capability "analyst", no four-eyes).
// A signed worklist is the precondition for the reviewer hand-off + sample completeness (enforced
// server-side; the panel gates fail-fast on selectIsWorklistSigned).
//
// Props: sampleId, analysisId, analysisDefinitionId, sampleLabel (display), primaryAssetId (the
// analysis's primary instrument id), reagentLotIds (the analysis's chosen consumable ids), onClose.
export default function WorklistModal({
  sampleId,
  analysisId,
  analysisDefinitionId,
  sampleLabel,
  primaryAssetId = null,
  reagentLotIds = [],
  onClose,
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const template = useSelector(
    (state) => limsWorklistTemplates.selectEffectiveForDefinition(state, analysisDefinitionId),
    shallowEqual
  )
  const worklist = useSelector(
    (state) => limsSampleWorklists.selectForSampleAnalysis(state, sampleId, analysisId),
    shallowEqual
  )
  // The asset register lives on the `asset` (singular) slice — `state.asset.assets`. Guarded like
  // QaDecisionSection so a not-yet-loaded slice can't crash the modal (the wrong `state.assets`
  // path threw "Cannot read properties of undefined (reading 'assets')" and broke the whole modal).
  const assetMap = useSelector((state) => (state.asset && state.asset.assets) || {}, shallowEqual)
  // The consumable register — resolves the analysis's reagent-lot ids to names (mirrors
  // QaDecisionSection's FORBRUGSVARER line). Self-fetched below (the panel loads it only
  // conditionally), so the read-only consumables show even in a fresh draft.
  const consumablesRegister = useSelector(limsConsumables.selectConsumables, shallowEqual)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [signing, setSigning] = useState(false)
  // Local editable state, seeded from the server worklist once loaded.
  const [completed, setCompleted] = useState({}) // { [stepId]: { checked, note } }

  const steps = useMemo(() => (template && Array.isArray(template.steps) ? template.steps : []), [template])
  const signed = !!(worklist && worklist.state === SAMPLE_WORKLIST_STATES.SIGNED)

  // Fetch-or-create on open: the effective template (for the steps) + the per-sample worklist (with
  // the auto-frozen context). The whole template set is loaded so the effective selector resolves.
  useEffect(() => {
    let alive = true
    setLoading(true)
    // Load the asset + consumable registers too, so the read-only instrument + consumables can be
    // shown by NAME (the analysis carries the ids; the names come from these registers).
    dispatch(assets.fetch()).catch(() => {})
    dispatch(limsConsumables.fetch()).catch(() => {})
    Promise.all([
      dispatch(limsWorklistTemplates.fetch()),
      dispatch(limsSampleWorklists.fetchOrCreate({ sampleId, analysisId, analysisDefinitionId })),
    ])
      .catch(() => dispatch(message.error(t("limsWorklistLoadFailed"))))
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [dispatch, sampleId, analysisId, analysisDefinitionId, t])

  // Seed local edit state from the loaded worklist (once, and whenever the persisted record changes
  // — e.g. after a save round-trip).
  useEffect(() => {
    if (!worklist) return
    const map = {}
    ;(worklist.completedSteps || []).forEach((s) => {
      if (s && s.stepId) map[s.stepId] = { checked: !!s.checked, note: s.note || "" }
    })
    setCompleted(map)
  }, [worklist])

  const setStepChecked = (stepId, checked) =>
    setCompleted((prev) => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), checked } }))
  const setStepNote = (stepId, note) =>
    setCompleted((prev) => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), note } }))

  const buildPatch = () => ({
    completedSteps: steps.map((s) => ({
      stepId: s.id,
      checked: !!(completed[s.id] && completed[s.id].checked),
      note: (completed[s.id] && completed[s.id].note) || "",
    })),
  })

  // #4072: the signature attests the prep WAS performed, so every step must be ticked first. The
  // server refuses with a 422 either way — this only saves the analyst a round-trip and says why
  // the button is ghosted. A template with no steps has nothing to complete.
  const outstandingSteps = steps.filter((s) => !(completed[s.id] && completed[s.id].checked)).length
  const allStepsChecked = outstandingSteps === 0

  const handleSave = () => {
    if (!worklist || signed) return
    setSaving(true)
    dispatch(limsSampleWorklists.save(worklist.id, buildPatch()))
      .then(() => dispatch(message.success(t("limsWorklistSaved"))))
      .catch((err) => dispatch(message.error((err && err.message) || t("limsWorklistSaveFailed"))))
      .finally(() => setSaving(false))
  }

  // The analyst sign: the password rides on the SAME PATCH that flips state to "signed" (with the
  // final steps). The instrument + consumables are derived/frozen server-side from the analysis, not
  // sent from here. Returns the promise so the dialog surfaces a backend 403/409 inline.
  const handleSign = (signature) => {
    if (!worklist) return undefined
    return dispatch(limsSampleWorklists.sign(worklist.id, signature, buildPatch()))
      .then(() => {
        setSigning(false)
        dispatch(message.success(t("limsWorklistSignedMsg")))
      })
  }

  const forskrift = worklist && worklist.forskriftVersionRef

  // Instrument (read-only): a SIGNED worklist shows the frozen equipmentAssetId (the GMP record of
  // what was used); a DRAFT shows the analysis's live primary instrument, falling back to the
  // server-derived equipmentAssetId (the BE derives it from the same analysis instrument) so the
  // instrument still shows when the planned-analysis hasn't loaded its asset requirements yet.
  // Resolved to a name via the asset register; falls back to a neutral "unknown instrument" label.
  const instrumentAssetId = signed
    ? (worklist && worklist.equipmentAssetId) || null
    : primaryAssetId || (worklist && worklist.equipmentAssetId) || null
  const instrumentName = useMemo(() => {
    if (!instrumentAssetId) return ""
    const a = assetMap[instrumentAssetId]
    return (a && a.name) || t("limsWorklistUnknownAsset")
  }, [instrumentAssetId, assetMap, t])

  // Consumables (read-only), by NAME only — mirrors the QA review's FORBRUGSVARER line. A SIGNED
  // worklist shows the frozen snapshot (what was actually used); a DRAFT resolves the analysis's
  // live reagent-lot selection against the consumable register.
  const consumableNames = useMemo(() => {
    if (signed) {
      const used = (worklist && worklist.consumableSnapshot && worklist.consumableSnapshot.used) || []
      return used.map((c) => c.name).filter(Boolean)
    }
    return (reagentLotIds || [])
      .map((id) => {
        const c = consumablesRegister.find((x) => x.id === id)
        return c ? c.name : null
      })
      .filter(Boolean)
  }, [signed, worklist, reagentLotIds, consumablesRegister])

  return (
    <Popup title={t("limsWorklistTitle", { sample: sampleLabel || "" })} onCancel={onClose}>
      <div className="WorklistModal">
        {loading ? (
          <p className="WorklistModal-message">{t("limsWorklistLoading")}</p>
        ) : !template ? (
          <p className="WorklistModal-message">{t("limsWorklistNoTemplate")}</p>
        ) : (
          <>
            <div className="WorklistModal-status">
              {signed ? (
                <StatusBadge variant="pass">{t("limsWorklistSignedBadge")}</StatusBadge>
              ) : (
                <StatusBadge variant="warn">{t("limsWorklistDraftBadge")}</StatusBadge>
              )}
              {signed && worklist.analystSignature && (
                <span className="WorklistModal-signed-by">
                  {t("limsWorklistSignedBy", {
                    initials: worklist.analystSignature.initials || "—",
                    at: worklist.analystSignedAt ? new Date(worklist.analystSignedAt).toLocaleString() : "",
                  })}
                </span>
              )}
            </div>

            {/* "Det der er brugt": forskrift version + the analysis's instrument + consumables, all
                READ-ONLY (draft = the analysis's live selection; signed = the frozen snapshot). */}
            <section className="WorklistModal-context">
              <Title>{t("limsWorklistUsedTitle")}</Title>
              <div className="WorklistModal-context-row">
                <span className="WorklistModal-context-label">{t("limsWorklistForskrift")}:</span>{" "}
                {forskrift ? (
                  <span>
                    {forskrift.documentName} · {t("limsForskriftVersion")} {forskrift.versionNumber}
                  </span>
                ) : (
                  <span className="WorklistModal-muted">{t("limsWorklistNoForskrift")}</span>
                )}
              </div>
              <div className="WorklistModal-context-row">
                <span className="WorklistModal-context-label">{t("limsWorklistConsumables")}:</span>{" "}
                {consumableNames.length > 0 ? (
                  <span>{consumableNames.join(", ")}</span>
                ) : (
                  <span className="WorklistModal-muted">{t("limsWorklistNoConsumables")}</span>
                )}
              </div>
              {/* Instrument: READ-ONLY — the analysis's primary instrument (draft) or the frozen
                  one (signed). The analyst does not pick it here; it belongs to the analysis. */}
              <div className="WorklistModal-context-row">
                <span className="WorklistModal-context-label">{t("limsWorklistInstrument")}:</span>{" "}
                {instrumentName ? (
                  <span>{instrumentName}</span>
                ) : (
                  <span className="WorklistModal-muted">{t("limsWorklistNoInstrument")}</span>
                )}
              </div>
            </section>

            {/* The steps checklist. Each step: instruction + a check + an optional note. */}
            <section className="WorklistModal-steps">
              <Title>{t("limsWorklistStepsTitle")}</Title>
              {steps.length === 0 ? (
                <p className="WorklistModal-message">{t("limsWorklistNoSteps")}</p>
              ) : (
                <ol className="WorklistModal-step-list">
                  {steps.map((step) => {
                    const state = completed[step.id] || {}
                    const isCompletion = step.kind === "completion"
                    return (
                      <li key={step.id} className={"WorklistModal-step" + (isCompletion ? " is-completion" : "")}>
                        <label className="WorklistModal-step-head">
                          <Checkbox
                            checked={!!state.checked}
                            onChange={(v) => setStepChecked(step.id, v)}
                            disabled={signed || saving}
                          />
                          <span className="WorklistModal-step-instruction">{step.instruction}</span>
                          {isCompletion && (
                            <span className="WorklistModal-step-tag">{t("limsWorklistStepKindCompletion")}</span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="WorklistModal-step-note"
                          placeholder={t("limsWorklistStepNote")}
                          value={state.note || ""}
                          onChange={(e) => setStepNote(step.id, e.target.value)}
                          disabled={signed || saving}
                        />
                      </li>
                    )
                  })}
                </ol>
              )}
            </section>

            {!signed && !allStepsChecked && (
              <p className="WorklistModal-hint">
                {t("limsWorklistSignNeedsAllSteps", { count: outstandingSteps })}
              </p>
            )}

            <div className="WorklistModal-actions">
              <Button variant="light" onClick={onClose}>
                {t("close")}
              </Button>
              {!signed && (
                <>
                  <Button variant="outline-primary" onClick={handleSave} disabled={saving}>
                    {t("limsWorklistSave")}
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => setSigning(true)}
                    disabled={saving || !allStepsChecked}
                    title={allStepsChecked ? undefined : t("limsWorklistSignNeedsAllSteps", { count: outstandingSteps })}
                  >
                    {t("limsWorklistSign")}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Analyst sign — capability "analyst", NO four-eyes (the analyst signs their own prep). The
          password rides on the same PATCH that flips the worklist to "signed". */}
      {signing && (
        <ESignatureModal
          title={t("limsWorklistSign")}
          requiredCapability="analyst"
          excludedMemberIds={[]}
          onSign={handleSign}
          onCancel={() => setSigning(false)}
        />
      )}
    </Popup>
  )
}
